// GraphQL
import { query } from "svelte-apollo";
import {
  GET_PICKUP_STOCK_SITES,
  GET_DELIVERY_STOCK_SITES,
  GET_DELIVERY_AND_PICKUP_STOCK_SITES,
} from "@modern/queries/stock_sites/stockSite";

// Services
import { HttpPromise } from "@shared/svelte/Modules/ApiService";
import { updateFulfillmentMethod } from "@retailer/services/fulfillment_methods_service";

// Stores
import RetailerFulfillmentMethodsStore from "@retailer/svelte/stores/RetailerFulfillmentMethodsStore";

// Types
import type {
  CartOrderType,
  FulfillmentInformationType,
  CartStockSiteType,
  TimeSlotType,
} from "@shared/types/Cart";
import type { CartProduct } from "@shared/types/ProductLine";

import { OHLQ_DISTRIBUTOR_ID } from "@shared/services/retailer_user_authentication/constants";

export const OHLQNumberOfDays = 14;
export const defaultNumberOfDays = 7;

export const isOHLQOrder = (distributor_id: number): boolean =>
  distributor_id === OHLQ_DISTRIBUTOR_ID;

const formatVIPWarehouseLocationCode = (locationCode: string | null): string | null => {
  return locationCode && locationCode.startsWith("VIP_") ? locationCode.substring(4) : locationCode;
};

export const getStockSiteFulfillmentData = (order: CartOrderType) => {
  const pickupOrderWarehouses = order.order_warehouses.filter(
    order_warehouse => order_warehouse.warehouse_id !== null,
  );
  const deliveryOrderWarehouses = order.order_warehouses.filter(
    order_warehouse => order_warehouse.warehouse_id === null,
  );
  const distributorId = order.distributor.id.toString();
  const customerId = order.distributor?.account_number?.toString();
  const timeSlotParam = {
    numberOfDays:
      order.distributor.id === OHLQ_DISTRIBUTOR_ID ? OHLQNumberOfDays : defaultNumberOfDays,
  };

  if (!customerId) {
    return;
  }

  let stockSiteQuery;
  let variables;
  const pickupStockSiteIds = pickupOrderWarehouses.map(order_warehouse =>
    formatVIPWarehouseLocationCode(order_warehouse.location_code),
  );
  const deliveryStockSiteIds = order.distributor.default_stock_site_id
    ? [formatVIPWarehouseLocationCode(order.distributor.default_stock_site_id)]
    : null;

  if (pickupOrderWarehouses.length && deliveryOrderWarehouses.length) {
    stockSiteQuery = GET_DELIVERY_AND_PICKUP_STOCK_SITES;
    variables = {
      distributorId,
      stockSiteInput: {
        eligibility: {
          customerId,
        },
        stockSiteIds: deliveryStockSiteIds,
      },
      stockSiteInput2: {
        eligibility: {
          customerId,
        },
        stockSiteIds: pickupStockSiteIds,
      },
      timeSlotInput: timeSlotParam,
      eligibility: {
        customerId,
      },
    };
  } else if (pickupOrderWarehouses.length) {
    stockSiteQuery = GET_PICKUP_STOCK_SITES;
    variables = {
      distributorId,
      stockSiteInput: {
        eligibility: {
          customerId,
        },
        stockSiteIds: pickupStockSiteIds,
      },
      timeSlotInput: timeSlotParam,
      eligibility: {
        customerId,
      },
    };
  } else if (deliveryOrderWarehouses.length) {
    stockSiteQuery = GET_DELIVERY_STOCK_SITES;
    variables = {
      distributorId,
      stockSiteInput: {
        eligibility: {
          customerId,
        },
        stockSiteIds: deliveryStockSiteIds,
      },
      timeSlotInput: timeSlotParam,
      eligibility: {
        customerId,
      },
    };
  }

  return query(stockSiteQuery, { variables });
};

export const getFulfillmentInformation = (
  order: CartOrderType,
  deliveryStockSites: CartStockSiteType[] = [],
  pickupStockSites: CartStockSiteType[] = [],
): FulfillmentInformationType[] => {
  let fulfillmentInfo = [];
  if (deliveryStockSites.length) {
    fulfillmentInfo = deliveryStockSites.map(stockSite =>
      formattedStockSiteFulfillmentData(order, stockSite, true),
    );
  }
  if (pickupStockSites.length) {
    let pickupData = pickupStockSites.map(stockSite =>
      formattedStockSiteFulfillmentData(order, stockSite, false),
    );
    fulfillmentInfo.push(...pickupData);
  }

  return fulfillmentInfo;
};

const formattedStockSiteFulfillmentData = (
  order: CartOrderType,
  stockSite: CartStockSiteType,
  isDelivery: boolean,
): FulfillmentInformationType => {
  const {
    stockSiteId,
    name,
    formattedName,
    formattedAddress,
    pickUpDates,
    deliveryDates,
    pickUpTimeSlots,
    deliveryTimeSlots,
    paymentMethods,
  } = stockSite;
  const hasPickupTimes = pickUpTimeSlots?.length > 0 && !!pickUpTimeSlots[0].specificTimes;
  const hasDeliveryTimes = deliveryTimeSlots?.length > 0 && !!deliveryTimeSlots[0].specificTimes;
  const hasPaymentMethods = paymentMethods?.length > 0;
  const defaultPickupDate = pickUpTimeSlots ? getDateString(pickUpTimeSlots, pickUpDates[0]) : null;
  const defaultDeliveryDate = deliveryTimeSlots
    ? getDateString(deliveryTimeSlots, deliveryDates[0])
    : null;
  const defaultFulfillmentDate = defaultPickupDate || defaultDeliveryDate;
  const defaultDeliveryTime = hasDeliveryTimes
    ? getTwelveHourTime(deliveryTimeSlots, deliveryDates[0])
    : null;
  const defaultPickupTime = hasPickupTimes
    ? getTwelveHourTime(pickUpTimeSlots, pickUpDates[0])
    : null;
  const defaultFulfillmentTime = defaultDeliveryTime || defaultPickupTime;
  const orderWarehouse = isDelivery
    ? order.order_warehouses.find(order_warehouse => order_warehouse.location_code === null)
    : order.order_warehouses.find(
        order_warehouse =>
          formatVIPWarehouseLocationCode(order_warehouse.location_code) === stockSiteId,
      );

  return {
    distributor_name: order.distributor.name,
    distributor_id: order.distributor.id,
    available_payment_methods: paymentMethods,
    delivery_fulfillment_dates: deliveryDates,
    delivery_fulfillment_times: deliveryTimeSlots,
    pickup_fulfillment_dates: pickUpDates,
    pickup_fulfillment_times: pickUpTimeSlots,
    show_fulfillment_times: hasPickupTimes || hasDeliveryTimes,
    show_payment_methods: hasPaymentMethods,
    order_warehouses: [
      {
        id: orderWarehouse?.id,
        warehouse_id: orderWarehouse?.warehouse_id,
        location_code: stockSiteId,
        name,
        formatted_name: formattedName,
        address: formattedAddress,
        fulfillment_date: defaultFulfillmentDate,
        fulfillment_time: defaultFulfillmentTime,
      },
    ],
    order: {
      id: order.id,
      intended_payment_method: stockSite.paymentMethods[0],
    },
  };
};

export const getDateString = (timeSlots: TimeSlotType[], date: Date): Date => {
  return timeSlots.find(slot => slot.date.formattedDate === date)?.date?.dateString;
};

const getTwelveHourTime = (timeSlots: TimeSlotType[], date: Date): string => {
  const timeSlotSpecificTimes = timeSlots.find(slot => slot.date.formattedDate === date)
    ?.specificTimes;
  return timeSlotSpecificTimes ? timeSlotSpecificTimes[0].twelveHour : null;
};

const batchUpdateCartProducts = async (
  products: CartProduct[],
  stockSiteId: string,
): Promise<void> => {
  const cartProducts = products.map(product => {
    return {
      id: product.id,
      warehouse_location_code: stockSiteId,
    };
  });

  await HttpPromise({
    url: "/api/retailer/cart_products/batch_update_warehouse_id",
    method: "POST",
    data: { cart_products: cartProducts },
  });
};

export const updateOrderFulfillmentMethod = async (
  distributorId: number,
  products: CartProduct[],
  stockSiteId: string,
): Promise<void> => {
  try {
    await Promise.all([
      batchUpdateCartProducts(products, stockSiteId),
      updateFulfillmentMethod(distributorId, stockSiteId),
      RetailerFulfillmentMethodsStore.updateDistributorFulfillmentMethod(distributorId),
    ]);
  } catch (e) {
    console.error(e);
  }
};
